import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  ShieldCheck, 
  Wrench, 
  LayoutGrid, 
  RectangleHorizontal,
  AlertTriangle,
  Store
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const services = [
  {
    id: 1,
    icon: ShieldCheck,
    title: 'Windshield Repair',
    description: 'Expert chip and crack repair to restore your windshield clarity and prevent further damage.',
    color: 'from-cyan-500/20 to-blue-500/20'
  },
  {
    id: 2,
    icon: Wrench,
    title: 'Windshield Replacement',
    description: 'Complete windshield replacement with OEM quality glass and professional installation.',
    color: 'from-purple-500/20 to-pink-500/20'
  },
  {
    id: 3,
    icon: LayoutGrid,
    title: 'Side Glass Replacement',
    description: 'Quick and precise side window replacement for all vehicle makes and models.',
    color: 'from-green-500/20 to-emerald-500/20'
  },
  {
    id: 4,
    icon: RectangleHorizontal,
    title: 'Rear Glass Replacement',
    description: 'Professional rear windshield replacement with perfect fit and seal guarantee.',
    color: 'from-orange-500/20 to-amber-500/20'
  },
  {
    id: 5,
    icon: AlertTriangle,
    title: 'Emergency Glass Repair',
    description: '24/7 emergency service for immediate assistance when you need it most.',
    color: 'from-red-500/20 to-rose-500/20'
  },
  {
    id: 6,
    icon: Store,
    title: 'Wholesale Auto Glass',
    description: 'Bulk orders for dealers and workshops. AIS authorized wholesaler with best prices.',
    color: 'from-blue-500/20 to-indigo-500/20'
  }
];

const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);
  const Icon = service.icon;
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-10px)`;
  };
  
  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
    }
  };

  return (
    <div
      ref={cardRef}
      className="service-card glass-card rounded-2xl p-6 lg:p-8 relative overflow-hidden group cursor-pointer"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid={`service-card-${service.id}`}
      style={{ transitionDuration: '0.3s' }}
    >
      {/* Gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
      
      {/* Glow effect on hover */}
      <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Icon */}
        <div className="w-16 h-16 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-6 group-hover:border-cyan-500/60 group-hover:bg-cyan-500/20 transition-all duration-500">
          <Icon className="w-8 h-8 text-cyan-400 group-hover:icon-glow transition-all duration-500" />
        </div>
        
        {/* Title */}
        <h3 className="font-orbitron text-xl lg:text-2xl font-bold text-white mb-3 uppercase tracking-wide group-hover:text-cyan-300 transition-colors duration-300">
          {service.title}
        </h3>
        
        {/* Description */}
        <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
          {service.description}
        </p>
        
        {/* Decorative line */}
        <div className="absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r from-cyan-500 to-transparent group-hover:w-full transition-all duration-700"></div>
      </div>
      
      {/* Corner decoration */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    </div>
  );
};

export default function ServicesSection() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse'
          }
        }
      );
      
      // Cards stagger animation
      gsap.fromTo(
        '.service-card',
        { opacity: 0, y: 80, rotateX: -15 },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 75%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="services" 
      className="relative py-20 lg:py-32 overflow-hidden"
      data-testid="services-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#050505]"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className="inline-block text-cyan-400 text-sm font-semibold tracking-[0.3em] uppercase mb-4">
            What We Offer
          </span>
          <h2 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-wider mb-6">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Professional auto glass services with cutting-edge technology and expert craftsmanship
          </p>
        </div>
        
        {/* Services grid */}
        <div ref={cardsRef} className="bento-grid">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
